import React, { createContext } from 'react';
import { v4 as uuidv4 } from 'uuid';

/* eslint-disable no-console */

import {
  finleyAssert,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import { TapeBuilderUI } from '@finley-technologies/tape-builder';

import { BorrowingBasePipelineArtifactInstanceGQLType } from 'types/dashboard-admin-gqlTypes.generated';

export type TapeBuilderMode = 'View' | 'Edit' | 'QA' | 'Monitor';

export type OrgSlugToOrgMapType = {
  [orgSlug: string]: {
    id: string;
    name: string;
    slug: string;
  };
};

type IDAndSlug = {
  id: string;
  slug: string;
};

type TapeBuilderEdge = TapeBuilderUI.TapeBuilderEdge & { id: string };

export type GraphBasicState = {
  nodes: TapeBuilderUI.TapeBuilderNodeAbstract[];
  edges: TapeBuilderEdge[];
};

export type GraphState = GraphBasicState & {
  artifactDocumentId: string;
  loanProducts: IDAndSlug[];
  creditFacilities: IDAndSlug[];
  orgSlug: string;
  dirty: boolean;
  mode: TapeBuilderMode;
  artifactInstances: BorrowingBasePipelineArtifactInstanceGQLType[] | null;
  selectedTags: TAG[];
  selectedNodeId: string | null;
};

export type StoredGraphState = {
  nodes: TapeBuilderUI.TapeBuilderNodeAbstract[];
  edges: TapeBuilderEdge[];
};

type ActionClearGraph = { type: 'clearGraph' };
type ActionSetGraph = { type: 'setGraph'; graph: GraphState };
type ActionAddNode = {
  type: 'addNode';
  node: TapeBuilderUI.TapeBuilderNodeAbstract;
};
type ActionAddEdge = { type: 'addEdge'; edge: TapeBuilderEdge };
type ActionDeleteEdge = { type: 'deleteEdge'; edge: TapeBuilderEdge };
type ActionDeleteNode = { type: 'deleteNode'; nodeId: string };

type ActionUpdateNodeConfig = {
  type: 'updateNodeConfig';
  nodeId: string;
  config: any;
};

export const tagTypeIterable = [
  'creditFacilitySlug',
  'artifactSlug',
  'nodeId',
  'nodetype',
] as const;

export type TAGTYPE = typeof tagTypeIterable[number];
export type TAG = {
  type: TAGTYPE;
  tag: string;
};

export const tagToString = (tag: TAG) => `${tag.type}:${tag.tag}`;
export const stringToTag = (tagString: string): TAG => {
  const [type, tag] = tagString.split(':');
  return { type: type as TAGTYPE, tag };
};

type ActionToggleTag = { type: 'toggleTag'; tag: TAG };
type ActionClearTags = { type: 'clearTags' };
type ActionSetSelectedNode = { type: 'setSelectedNode'; nodeId: string | null };

type GraphAction =
  | ActionClearGraph
  | ActionAddEdge
  | ActionAddNode
  | ActionDeleteEdge
  | ActionDeleteNode
  | ActionSetGraph
  | ActionToggleTag
  | ActionClearTags
  | ActionUpdateNodeConfig
  | ActionSetSelectedNode;

type Dispatch = (action: GraphAction) => void;

// this context is shared between components
// TapeBuilderControl is the context provider
// and other components are consumers
export type GraphContextType = {
  state: GraphState;
  dispatch: Dispatch;
};

const GraphContext = createContext<GraphContextType | null>(null);

const generateNodeTags = (
  node: TapeBuilderUI.TapeBuilderNodeAbstract,
): TAG[] => {
  const nodeTagsStrings = new Set<string>();
  tagTypeIterable.forEach((key) => {
    if (key in node.configuration) {
      // @ts-ignore
      const value = node.configuration[key];
      nodeTagsStrings.add(tagToString({ type: key, tag: value }));
    }
  });

  nodeTagsStrings.add(
    tagToString({ type: 'nodetype', tag: node.metadata.tapeBuilderNodeType }),
  );

  return Array.from(nodeTagsStrings).map(stringToTag);
};

export const addIdsToEdges = (
  edges: TapeBuilderUI.TapeBuilderEdge[],
): TapeBuilderEdge[] => {
  return edges.map((edge) => ({
    ...edge,
    id: `${edge.source}:${edge.target}`,
  }));
};

// every artifact slug, operation and credit facility slug is a tag
// we use this function to generate tags from the graph state
export const generateTags = (graphContext: GraphContextType): TAG[] => {
  const tagStrings = new Set<string>();
  graphContext.state.nodes.forEach((node) => {
    const nodeTags = generateNodeTags(node);
    nodeTags.forEach((tag) => {
      tagStrings.add(tagToString(tag));
    });
  });
  return Array.from(tagStrings).map(stringToTag);
};

export const getDefaultGraphState = ({
  mode,
}: {
  mode: TapeBuilderMode;
}): GraphState => {
  const defaultGraphState: GraphState = {
    nodes: [],
    edges: [],
    creditFacilities: [],
    loanProducts: [],
    artifactDocumentId: `initial document: ${Date.now()}`,
    orgSlug: 'placeholder', // this will be replaced later when we get the query status.
    dirty: false,
    mode,
    artifactInstances: null,
    selectedTags: [],
    selectedNodeId: null,
  };
  return defaultGraphState;
};

export const getNewNodeTag = (nodeId: string): TAG => ({
  type: 'nodeId',
  tag: nodeId,
});

const isNodeTaggedInternal = (state: GraphState, nodeId: string): boolean => {
  const node = state.nodes.find(
    ({ metadata }) => metadata.tapeBuilderNodeId === nodeId,
  );
  if (!node) {
    return false;
  }

  if (state.selectedTags.length === 0) {
    return true;
  }

  const nodeTags = generateNodeTags(node);

  // for new nodes we add their node ids in tags so
  // they are always visible.
  nodeTags.push(getNewNodeTag(nodeId));

  const selectedTagSet = new Set(state.selectedTags.map(tagToString));

  return nodeTags.some((tag) => selectedTagSet.has(tagToString(tag)));
};

const getVisibleGraphInternal = (state: GraphState): StoredGraphState => {
  const taggedNodes = new Set(
    state.nodes
      .filter((node) =>
        isNodeTaggedInternal(state, node.metadata.tapeBuilderNodeId),
      )
      .map((node) => node.metadata.tapeBuilderNodeId),
  );

  // add any nodes directly connected to nodes.
  const connectedNodes = new Set<string>();
  state.edges.forEach(({ source, target }) => {
    if (taggedNodes.has(source)) {
      connectedNodes.add(target);
    }
    if (taggedNodes.has(target)) {
      connectedNodes.add(source);
    }
  });

  const nodes = state.nodes.filter(
    (node) =>
      taggedNodes.has(node.metadata.tapeBuilderNodeId) ||
      connectedNodes.has(node.metadata.tapeBuilderNodeId),
  );

  const visibleNodeIds = new Set(
    nodes.map(({ metadata }) => metadata.tapeBuilderNodeId),
  );

  const edges = state.edges.filter(({ source, target }) => {
    return visibleNodeIds.has(source) && visibleNodeIds.has(target);
  });
  return { nodes, edges };
};

export const graphReducer = (
  state: GraphState,
  action: GraphAction,
): GraphState => {
  switch (action.type) {
    case 'addNode': {
      // if we have any selected tags,
      // we need to tag this newly added node
      // so that it shows visible.
      const selectedTags: TAG[] =
        state.selectedTags.length === 0
          ? []
          : [
              ...state.selectedTags,
              getNewNodeTag(action.node.metadata.tapeBuilderNodeId),
            ];

      return {
        ...state,
        selectedTags,
        nodes: [...state.nodes, action.node],
        dirty: true,
      };
    }
    case 'addEdge':
      return {
        ...state,
        edges: [...state.edges, action.edge],
        dirty: true,
      };

    case 'deleteEdge': {
      const edges = state.edges.filter((edge) => {
        return !(
          edge.source === action.edge.source &&
          edge.target === action.edge.target
        );
      });
      return {
        ...state,
        edges,
        dirty: true,
      };
    }

    case 'clearGraph': {
      // we will clear only visible (filtered) graph.
      // if no filter is applied, then everything will be cleared.
      const visibleGraph = getVisibleGraphInternal(state);
      const visibleNodeIds = new Set(
        visibleGraph.nodes.map(({ metadata }) => metadata.tapeBuilderNodeId),
      );

      const nodes = state.nodes.filter(
        ({ metadata }) => !visibleNodeIds.has(metadata.tapeBuilderNodeId),
      );

      const edges = state.edges.filter(
        ({ source, target }) =>
          !visibleNodeIds.has(source) && !visibleNodeIds.has(target),
      );

      return {
        ...state,
        nodes,
        edges,
        dirty: true,
      };
    }

    case 'setGraph': {
      return {
        ...state,
        ...action.graph,
      };
    }

    case 'updateNodeConfig': {
      const nodes = state.nodes.map((node) => {
        if (node.metadata.tapeBuilderNodeId === action.nodeId) {
          return {
            ...node,
            configuration: action.config,
          };
        }
        return node;
      });
      return {
        ...state,
        nodes,
        dirty: true,
      };
    }

    case 'deleteNode': {
      const nodes = state.nodes.filter(
        (node) => node.metadata.tapeBuilderNodeId !== action.nodeId,
      );
      // delete any edges that are connected to this node
      const edges = state.edges.filter((edge) => {
        return !(
          edge.source === action.nodeId || edge.target === action.nodeId
        );
      });

      return {
        ...state,
        nodes,
        edges,
        dirty: true,
      };
    }

    case 'toggleTag': {
      const selectedTagSet = new Set(state.selectedTags.map(tagToString));
      if (selectedTagSet.has(tagToString(action.tag))) {
        return {
          ...state,
          selectedTags: state.selectedTags.filter(
            (tag) => tagToString(tag) !== tagToString(action.tag),
          ),
        };
      }
      return {
        ...state,
        selectedTags: [...state.selectedTags, action.tag],
      };
    }

    case 'clearTags': {
      return {
        ...state,
        selectedTags: [],
      };
    }

    case 'setSelectedNode': {
      return {
        ...state,
        selectedNodeId: action.nodeId,
      };
    }

    default:
      return finleyAssertUnreachable(action);
  }
};

export const GraphProvider = ({
  children,
  graph,
  dispatch,
}: {
  children: React.ReactNode;
  graph: GraphState;
  dispatch: React.Dispatch<GraphAction>;
}) => {
  const value = { state: graph, dispatch };

  return (
    <GraphContext.Provider value={value}>{children}</GraphContext.Provider>
  );
};

export const useGraph = () => {
  const context = React.useContext(GraphContext);
  finleyAssert(context, 'useGraph must be used within a GraphProvider');
  return context;
};

export const toggleTag = (graphContext: GraphContextType, tag: TAG) => {
  const { dispatch } = graphContext;
  dispatch({ type: 'toggleTag', tag });
};

export const clearTags = (graphContext: GraphContextType) => {
  const { dispatch } = graphContext;
  dispatch({ type: 'clearTags' });
};

export const setSelectedNode = (
  graphContext: GraphContextType,
  nodeId: string | null,
) => {
  const { dispatch } = graphContext;
  dispatch({ type: 'setSelectedNode', nodeId });
};

export const getNewNodeParams = (graphContext: GraphContextType) => {
  const creditFacilitySlugs = graphContext.state.creditFacilities.map(
    ({ slug }) => slug,
  );
  const loanProductSlugs = graphContext.state.loanProducts.map(
    ({ slug }) => slug,
  );

  return {
    suggestedSerialNumber: uuidv4().slice(0, 6),
    id: uuidv4(),
    orgSlug: graphContext.state.orgSlug,
    artifactDocumentId: graphContext.state.artifactDocumentId,
    creditFacilitySlug:
      creditFacilitySlugs.length > 0
        ? creditFacilitySlugs.join(',')
        : 'creditFacilitySlugGoesHere',
    loanProductSlug:
      loanProductSlugs.length > 0
        ? loanProductSlugs.join(',')
        : 'loanProductSlugGoesHere',
  };
};

export const getGraphNodeById = (
  graphContext: GraphContextType,
  nodeId: string,
): TapeBuilderUI.TapeBuilderNodeAbstract | null => {
  const node = graphContext.state.nodes.find(
    ({ metadata }) => metadata.tapeBuilderNodeId === nodeId,
  );
  return node || null;
};

export const isNodeTagged = (
  graphContext: GraphContextType,
  nodeId: string,
): boolean => {
  return isNodeTaggedInternal(graphContext.state, nodeId);
};

// tagged nodes and nodes directly connected to tagged nodes
// and any edges joining them is visible graph.
export const getVisibleGraph = (
  graphContext: GraphContextType,
): StoredGraphState => {
  return getVisibleGraphInternal(graphContext.state);
};

export const isGraphFiltered = (graphContext: GraphContextType) => {
  return graphContext.state.selectedTags.length > 0;
};

export const getCreditFacilityBySlug = (
  graphContext: GraphContextType,
  creditFacilitySlug: string,
): string | null => {
  const entry = graphContext.state.creditFacilities.find(
    ({ slug }) => slug === creditFacilitySlug,
  );
  return entry ? entry.id : null;
};

export const getLoanProductBySlug = (
  graphContext: GraphContextType,
  loanProductSlug: string,
): string | null => {
  const entry = graphContext.state.loanProducts.find(
    ({ slug }) => slug === loanProductSlug,
  );
  return entry ? entry.id : null;
};

export const getLoanProductSlug = (
  graphContext: GraphContextType,
  loanProductId: string,
): string | null => {
  const entry = graphContext.state.loanProducts.find(
    ({ id }) => id === loanProductId,
  );
  return entry ? entry.slug : null;
};

export const getTargetNodes = (
  graphContext: GraphContextType,
  nodeId: string,
): TapeBuilderUI.TapeBuilderNodeAbstract[] => {
  const edges = graphContext.state.edges.filter(
    ({ source }) => source === nodeId,
  );

  const nodes = edges.map(({ target }) => {
    const node = getGraphNodeById(graphContext, target);
    finleyAssert(node, `Could not find node with id ${target}`);
    return node;
  });

  return nodes;
};

export const getAllOperationNodes = (
  graphContext: GraphContextType,
): TapeBuilderUI.TapeBuilderNodeAbstract[] => {
  return graphContext.state.nodes.filter(
    TapeBuilderUI.isTapeBuilderNativeOperationNode,
  );
};

export const getIncomingGenerateLoanTapeNode = ({
  tapeBuilderNode,
  graphContext,
  loanProductSlug,
}: {
  tapeBuilderNode: TapeBuilderUI.TapeBuilderNodeAbstract;
  graphContext: GraphContextType;
  loanProductSlug: string;
}): TapeBuilderUI.types.TapeNodes.generatedLoan.UINode => {
  const { state } = graphContext;

  // find nodes that go into given tapeBuilderNode.
  const nodes = state.edges
    .filter(
      ({ target }) => target === tapeBuilderNode.metadata.tapeBuilderNodeId,
    )
    .map(({ source }) => {
      const node = getGraphNodeById(graphContext, source);
      finleyAssert(node, `Could not find node with id ${source}`);
      return node;
    });

  const generatedLoanTapeNodes = nodes
    .filter(TapeBuilderUI.tapeNodes.file_generatedLoanTape.isNode)
    .filter((node) => {
      return node.configuration.loanProductSlug === loanProductSlug;
    });

  // convert to TapeBuilderGeneratedLoanTapeNode
  finleyAssert(
    generatedLoanTapeNodes.length === 1,
    `Found ${generatedLoanTapeNodes.length} matching generatedLoanTape nodes`,
  );

  return generatedLoanTapeNodes[0];
};

export const getArtifactInstance = ({
  tapeBuilderNode,
  graphContext,
}: {
  tapeBuilderNode: TapeBuilderUI.TapeBuilderNodeAbstract;
  graphContext: GraphContextType;
}): BorrowingBasePipelineArtifactInstanceGQLType | null => {
  // look for artifactInstance node that corresponds to  given tapeBuilderNode.
  const { artifactInstances, mode } = graphContext.state;

  if (mode !== 'Monitor' || !artifactInstances) {
    return null;
  }

  const artifactInstance = artifactInstances.find(({ artifactId }) => {
    return artifactId === tapeBuilderNode.metadata.tapeBuilderNodeId;
  });

  return artifactInstance || null;
};

export const getArtifactInstanceBySlug = ({
  slug,
  graphContext,
}: {
  slug: string;
  graphContext: GraphContextType;
}): BorrowingBasePipelineArtifactInstanceGQLType | null => {
  // look for artifactInstance node that corresponds to  given tapeBuilderNode.
  const { artifactInstances, mode, nodes } = graphContext.state;

  if (mode !== 'Monitor' || !artifactInstances) {
    return null;
  }

  // 1st find a node from the slug
  const artifactNode = nodes.find((node) => {
    return (
      'artifactSlug' in node.configuration &&
      node.configuration.artifactSlug === slug
    );
  });

  if (!artifactNode) {
    console.log('artifactNode not found for slug', slug);
    return null;
  }
  return getArtifactInstance({ tapeBuilderNode: artifactNode, graphContext });
};
